/**
 * advanceArithmetic.ts — what an advance's figures make true, derived.
 *
 * TWO QUESTIONS, AND BOTH OF THEM ARE THE PANEL'S RATHER THAN THE LEDGER'S:
 *
 * - WHAT THIS ADVANCE DOES TO THE WEEKLY TOTAL. Every advance is repaid weekly and its repayment is
 *   ADDED to the contract instalment [D-B54], so an admin typing 200 into the repayment area is
 *   raising what the driver owes every Monday and has to be able to see the number it becomes.
 * - WHAT THE SCHEDULE'S OWN TERM IS. The total repayable over the weekly figure, floored, with the
 *   half-pesewa dust guard and the final instalment by SUBTRACTION.
 *
 * THE FORWARD SUM IS COMPOSED HERE AND `weekly_rate` IS NOT READ — the whole point of the panel.
 * `get_contract_insights.weekly_rate` answers with the LAST REPAYMENT'S allocator total where one
 * exists, so on a contract that has been paying for months it states what the driver was billed
 * last week and not what they will be billed from this advance's first Monday. The panel is about
 * the future, so it composes the future's terms itself: the latest contract instalment, the latest
 * rate row of every insurance premium advance, the latest rate row of every other standing advance,
 * and the figure being typed.
 *
 * "LATEST ROW" IS `MAX(starts_at)` PER LOAN AND THE SUM OF EVERY ROW ON THAT DAY, which is the
 * server's own spelling (`ili.starts_at = (SELECT MAX(starts_at) … WHERE loan_id = il.id)`), not
 * "the last row of the array". Two rows sharing a loan's latest start date are both effective, and
 * that is exactly how a duplicated instalment inflates the rate.
 *
 * DISPLAY-ONLY, AND NAMED AS SUCH against `.agents/rules/financial-parity.md`: none of the five
 * protected numbers is recomputed here and nothing is written anywhere. This is arithmetic a driver
 * could do on the same page.
 */

import { termWeeks } from '../areas/financialsMath';

/**
 * One weekly instalment row, with the loan it belongs to.
 *
 * THE LOAN ID IS NOT DECORATION: the rule is per loan, so a flat list without it cannot be grouped
 * and would either sum a loan's whole rate history or drop a second loan's current rate.
 */
export interface WeeklyInstalmentRow {
    loanId: number;
    amount: number | null;
    /** A DATE column: `'YYYY-MM-DD'`, compared as a string. */
    startsAt: string | null;
}

export interface ForwardWeeklyTotal {
    /** The latest `contract_instalments.amount`. Null is a contract with no instalment recorded. */
    contract: number | null;
    /** Σ of every insurance premium advance's current weekly rate. */
    insuranceLoans: number;
    /** Σ of every OTHER standing advance's current weekly rate. */
    loans: number;
    /** The figure being typed, or null while the field is empty. */
    typed: number | null;
    /** What the driver owes every Monday once this advance starts. */
    total: number | null;
    /** What they owe today — the same sum without the typed figure. */
    before: number | null;
}

/**
 * Σ of each loan's CURRENT weekly rate — `MAX(starts_at)` per loan, every row on that day.
 *
 * A row with no start date is in no ordering at all and is dropped: the column is NOT NULL, so that
 * only ever happens where a select forgot to ask for it.
 */
function currentWeekly(rows: WeeklyInstalmentRow[]): number {
    const latest = new Map<number, string>();
    for (const row of rows) {
        if (!row.startsAt) continue;
        const held = latest.get(row.loanId);
        if (held === undefined || row.startsAt > held) latest.set(row.loanId, row.startsAt);
    }
    return rows.reduce((total, row) => {
        if (!row.startsAt) return total;
        return row.startsAt === latest.get(row.loanId) ? total + (row.amount ?? 0) : total;
    }, 0);
}

/**
 * What the weekly total becomes once this advance starts being repaid.
 *
 * THE CALLER PASSES THE OTHER ADVANCES, NOT ALL OF THEM. On an edit the advance being typed into is
 * already in `loan_instalments`, and leaving it in would count its old rate beside its new one —
 * the typed figure IS this advance's contribution, which is why it arrives separately.
 *
 * NULL WHERE THERE IS NO CONTRACT INSTALMENT, and not a zero: a contract whose weekly figure has
 * not been recorded has no total for this one to be added to, and stating the advance's own
 * repayment as "the weekly total" would be a figure nobody owes.
 */
export function forwardWeeklyTotal(
    contractInstalment: number | null,
    insuranceLoanInstalments: WeeklyInstalmentRow[],
    loanInstalments: WeeklyInstalmentRow[],
    typed: number | null,
): ForwardWeeklyTotal {
    const insuranceLoans = currentWeekly(insuranceLoanInstalments);
    const loans = currentWeekly(loanInstalments);
    const before =
        contractInstalment === null || contractInstalment === undefined
            ? null
            : contractInstalment + insuranceLoans + loans;

    return {
        contract: contractInstalment ?? null,
        insuranceLoans,
        loans,
        typed: typed ?? null,
        total: before === null ? null : before + (typed ?? 0),
        before,
    };
}

export interface DerivedTerm {
    /** How many Mondays, expected. Null wherever the Schedule states no term at all. */
    term: number | null;
    /** The last repayment, by SUBTRACTION — never by a percentage or a remainder. */
    finalInstalment: number | null;
}

/**
 * The term the Schedule prints, and the final repayment under it.
 *
 * `termWeeks` IS THE ONE DERIVATION AND IT IS IMPORTED RATHER THAN RESTATED: floor, the part-week by
 * subtraction, and an instalment added only where that remainder is worth half a pesewa or more
 * [D-T1]. `agreement_merge_data` applies the same guard to `{{contract.term_weeks}}`, and a `ceil`
 * here would put a whole extra Monday on a page a borrower signs.
 *
 * IT AMORTISES THE TOTAL REPAYABLE AND NOT THE PRINCIPAL. The interest is charged once at the
 * outset and is part of what the weekly repayment repays, so a term over the advance alone would
 * state a schedule that stops short of the figure printed two rows above it on the Schedule.
 */
export function derivedTerm(totalRepayable: number | null, weekly: number | null): DerivedTerm {
    const term = termWeeks(totalRepayable, weekly);
    if (term === null) return { term: null, finalInstalment: null };
    return {
        term,
        finalInstalment: (totalRepayable as number) - (term - 1) * (weekly as number),
    };
}

export interface AdvanceMoney {
    /** The Amount advanced — the disbursement, as it stands on the transaction. */
    principal: number | null;
    /** The Total amount repayable, HELD ONCE so the interest can be taken off it. */
    total: number | null;
    /** The Interest row, by SUBTRACTION off that total. */
    interest: number | null;
}

/**
 * The three money rows of the loan v7 Schedule, derived the way the server derives them.
 *
 * ONE TOTAL, HELD ONCE, READ TWICE. `agreement_merge_data` computes
 * `(v_loan_principal + v_loan_principal * COALESCE(v_loan_rate, 0) / 100)::numeric` and prints the
 * interest as `v_loan_total - v_loan_principal` — the Schedule states on the page that the third
 * row is the first two added, so the interest has to BE that subtraction rather than a second
 * computation of the same percentage that is not guaranteed to agree in double precision.
 *
 * THE GROUPING IS THE RESOLVER'S, CHARACTER FOR CHARACTER: `*` and `/` are left-associative at
 * equal precedence, so the multiply lands first and the divide second. `p * (r / 100)` is a
 * different rounding in the doubles these columns are stored in.
 *
 * A ZERO RATE IS A FIGURE AND NOT AN ABSENCE (`COALESCE(v_loan_rate, 0)`): an interest-free advance
 * repays exactly what was advanced.
 */
export function advanceMoney(principal: number | null, rate: number | null): AdvanceMoney {
    if (principal === null || principal === undefined) {
        return { principal: null, total: null, interest: null };
    }
    const total = principal + (principal * (rate ?? 0)) / 100;
    return { principal, total, interest: total - principal };
}
